/**
 * Cart Drawer — Interactive behavior
 * Handles open/close, quantity controls, remove, upsell add-to-cart,
 * and exposes window.OdditCartDrawer API.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-cart-drawer';
  var sectionState = new Map();
  var DEBOUNCE_DELAY = 300;
  var debounceTimers = {};

  function initSection(section) {
    var state = {
      section: section,
      panel: section.querySelector('.oddit-cart-drawer__panel'),
      overlay: section.querySelector('.oddit-cart-drawer__overlay'),
      closeBtn: section.querySelector('.oddit-cart-drawer__close'),
      body: section.querySelector('.oddit-cart-drawer__body'),
      focusableSelector: 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      documentMouseMove: null,
      documentMouseUp: null
    };

    sectionState.set(section.id || 'default', state);

    // Close button
    if (state.closeBtn) {
      state.closeBtn.addEventListener('click', close);
    }

    // Overlay click to close
    if (state.overlay) {
      state.overlay.addEventListener('click', close);
    }

    // Escape key to close
    document.addEventListener('keydown', handleKeydown);

    // Event delegation for quantity, remove, and add-to-cart
    section.addEventListener('click', handleSectionClick);

    // Init upsell carousel
    initCarousel(section, state);

    // Populate cross-sell from Recommendations API if configured
    fetchCrossSellRecommendations(section, state);

    // Expose public API
    window.OdditCartDrawer = {
      open: open,
      close: close,
      toggle: toggle,
      addToCart: addToCart,
      addMultipleToCart: addMultipleToCart,
      removeFromCart: removeFromCart,
      updateLineItemQuantity: updateLineItemQuantity,
      getCart: getCart,
      refreshCart: refreshCart
    };
  }

  function initCarousel(section, state) {
    var container = section.querySelector('[data-cross-sell-container]');
    var track = section.querySelector('.oddit-cart-drawer__upsell-track');
    var dotsContainer = section.querySelector('[data-carousel-dots]');
    var cards = section.querySelectorAll('.oddit-cart-drawer__upsell-card');
    var carousel = section.querySelector('.oddit-cart-drawer__upsell-carousel');
    if (container && cards.length > 0) {
      container.classList.add('has-products');
    }
    // Restore previous slide index if available, clamped to valid range
    var startIndex = state.carouselIndex || 0;
    if (startIndex >= cards.length) startIndex = 0;

    if (!track || cards.length < 2) {
      // Single card — make it active
      if (cards.length === 1) cards[0].classList.add('oddit-cart-drawer__upsell-card--active');
      return;
    }

    // Set starting card as active
    cards[startIndex].classList.add('oddit-cart-drawer__upsell-card--active');

    // Generate dots
    var dots = [];
    if (dotsContainer) {
      dotsContainer.innerHTML = '';
      for (var d = 0; d < cards.length; d++) {
        var dot = document.createElement('button');
        dot.className = 'oddit-cart-drawer__upsell-dot' + (d === startIndex ? ' oddit-cart-drawer__upsell-dot--active' : '');
        dot.setAttribute('type', 'button');
        dot.setAttribute('aria-label', 'Go to slide ' + (d + 1));
        dot.setAttribute('aria-current', d === startIndex ? 'true' : 'false');
        dotsContainer.appendChild(dot);
        dots.push(dot);
      }
    }

    var currentIndex = startIndex;
    var startX = 0;
    var currentX = 0;
    var isDragging = false;

    function goToSlide(index) {
      if (index < 0) index = 0;
      if (index >= cards.length) index = cards.length - 1;
      currentIndex = index;
      state.carouselIndex = currentIndex;
      cards.forEach(function (card, i) {
        card.classList.toggle('oddit-cart-drawer__upsell-card--active', i === currentIndex);
      });
      dots.forEach(function (dot, i) {
        dot.classList.toggle('oddit-cart-drawer__upsell-dot--active', i === currentIndex);
        dot.setAttribute('aria-current', i === currentIndex ? 'true' : 'false');
      });
    }

    // Dot click navigation
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        goToSlide(i);
      });
    });

    // Touch swipe support
    track.addEventListener('touchstart', function (e) {
      if (e.target.closest('[data-add-to-cart]')) return;
      isDragging = true;
      startX = e.touches[0].clientX;
    }, { passive: true });

    track.addEventListener('touchmove', function (e) {
      if (!isDragging) return;
      currentX = e.touches[0].clientX;
    }, { passive: true });

    track.addEventListener('touchend', function () {
      if (!isDragging) return;
      isDragging = false;
      var diff = startX - currentX;
      if (Math.abs(diff) > 40) {
        if (diff > 0) {
          goToSlide(currentIndex + 1);
        } else {
          goToSlide(currentIndex - 1);
        }
      }
    });

    // Mouse drag support
    track.addEventListener('mousedown', function (e) {
      if (e.target.closest('[data-add-to-cart]')) return;
      isDragging = true;
      startX = e.clientX;
      track.style.cursor = 'grabbing';
      e.preventDefault();
    });

    state.documentMouseMove = function (e) {
      if (!isDragging) return;
      currentX = e.clientX;
    };

    state.documentMouseUp = function () {
      if (!isDragging) return;
      isDragging = false;
      track.style.cursor = '';
      var diff = startX - currentX;
      if (Math.abs(diff) > 40) {
        if (diff > 0) {
          goToSlide(currentIndex + 1);
        } else {
          goToSlide(currentIndex - 1);
        }
      }
    };

    document.addEventListener('mousemove', state.documentMouseMove);
    document.addEventListener('mouseup', state.documentMouseUp);
  }

  function fetchCrossSellRecommendations(section, state) {
    var container = section.querySelector('[data-cross-sell-container]');
    if (!container) return;
    if (container.getAttribute('data-cross-sell-source') !== 'recommendations') return;

    var sectionEl = section.closest('[id^="shopify-section-"]');
    var sectionId = sectionEl ? sectionEl.id.replace('shopify-section-', '') : null;
    if (!sectionId) return;

    var intent = container.getAttribute('data-cross-sell-intent') || 'related';
    var limit = container.getAttribute('data-cross-sell-limit') || 4;

    getCart().then(function (cart) {
      if (!cart || !cart.items || cart.items.length === 0) return;
      // Most recently added line item — Shopify returns cart.items in the order added
      var lastItem = cart.items[cart.items.length - 1];
      var productId = lastItem && lastItem.product_id;
      if (!productId) return;

      var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var url = routeRoot +
        'recommendations/products?section_id=' + encodeURIComponent(sectionId) +
        '&product_id=' + encodeURIComponent(productId) +
        '&intent=' + encodeURIComponent(intent) +
        '&limit=' + encodeURIComponent(limit);

      return fetch(url).then(function (res) {
        if (!res.ok) throw new Error('Recommendations request failed');
        return res.text();
      }).then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newTrack = doc.querySelector('[data-cross-sell-products]');
        var oldTrack = section.querySelector('[data-cross-sell-products]');
        if (newTrack && oldTrack) {
          oldTrack.innerHTML = newTrack.innerHTML;
          initCarousel(section, state);
        }
      });
    }).catch(function (err) {
      console.error('[oddit-cart-drawer] Cross-sell recommendations error:', err);
    });
  }

  function getState() {
    var entries = sectionState.values();
    var first = entries.next();
    return first.value || null;
  }

  function open() {
    var state = getState();
    if (!state) return;

    state.section.classList.add('is-open');
    document.body.classList.add('oddit-cart-drawer-open');

    // Focus first focusable element
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var firstFocusable = state.panel.querySelector(state.focusableSelector);
        if (firstFocusable) {
          firstFocusable.focus();
        }
      });
    });
  }

  function close() {
    var state = getState();
    if (!state) return;

    state.section.classList.remove('is-open');
    document.body.classList.remove('oddit-cart-drawer-open');
  }

  function toggle() {
    var state = getState();
    if (!state) return;

    if (state.section.classList.contains('is-open')) {
      close();
    } else {
      open();
    }
  }

  function handleKeydown(e) {
    if (e.key === 'Escape') {
      var state = getState();
      if (state && state.section.classList.contains('is-open')) {
        close();
      }
    }
  }

  function handleSectionClick(e) {
    // Quantity minus
    var minusBtn = e.target.closest('[data-qty-minus]');
    if (minusBtn) {
      var lineItem = minusBtn.closest('[data-line-item-key]');
      var valueEl = minusBtn.parentElement.querySelector('.oddit-cart-drawer__quantity-value');
      if (valueEl && lineItem) {
        var current = parseInt(valueEl.textContent, 10) || 1;
        var newQty = current > 1 ? current - 1 : 0;
        valueEl.textContent = newQty > 0 ? newQty : 1;
        var key = lineItem.getAttribute('data-line-item-key');
        if (newQty === 0) {
          removeFromCart(key).then(function () {
            refreshCartUI();
          });
        } else {
          debouncedUpdateQuantity(key, newQty);
        }
      }
      return;
    }

    // Quantity plus
    var plusBtn = e.target.closest('[data-qty-plus]');
    if (plusBtn) {
      var lineItem2 = plusBtn.closest('[data-line-item-key]');
      var valueEl2 = plusBtn.parentElement.querySelector('.oddit-cart-drawer__quantity-value');
      if (valueEl2 && lineItem2) {
        var current2 = parseInt(valueEl2.textContent, 10) || 1;
        var newQty2 = current2 + 1;
        valueEl2.textContent = newQty2;
        var key2 = lineItem2.getAttribute('data-line-item-key');
        debouncedUpdateQuantity(key2, newQty2);
      }
      return;
    }

    // Remove item
    var removeBtn = e.target.closest('.oddit-cart-drawer__item-remove');
    if (removeBtn) {
      var item = removeBtn.closest('[data-line-item-key]');
      if (item) {
        var itemKey = item.getAttribute('data-line-item-key');
        item.style.opacity = '0.5';
        item.style.pointerEvents = 'none';
        removeFromCart(itemKey).then(function () {
          item.remove();
          refreshCartUI();
        }).catch(function () {
          item.style.opacity = '';
          item.style.pointerEvents = '';
        });
      }
      return;
    }

    // Add to cart (upsell)
    var addBtn = e.target.closest('[data-add-to-cart]');
    if (addBtn) {
      e.stopPropagation();
      var variantId = Number(addBtn.getAttribute('data-variant-id'));
      if (!variantId) return;

      var quantity = Number(addBtn.getAttribute('data-quantity')) || 1;
      var originalText = addBtn.textContent;
      addBtn.disabled = true;
      addBtn.classList.add('oddit-cart-drawer__upsell-add--loading');
      addBtn.textContent = '';

      addToCart(variantId, quantity)
        .then(function () {
          refreshCartUI();
        })
        .finally(function () {
          addBtn.disabled = false;
          addBtn.classList.remove('oddit-cart-drawer__upsell-add--loading');
          addBtn.textContent = originalText;
        });
    }
  }

  function debouncedUpdateQuantity(key, quantity) {
    if (debounceTimers[key]) {
      clearTimeout(debounceTimers[key]);
    }
    debounceTimers[key] = setTimeout(function () {
      delete debounceTimers[key];
      updateLineItemQuantity(key, quantity).then(function () {
        refreshCartUI();
      });
    }, DEBOUNCE_DELAY);
  }

  function refreshCartUI() {
    var drawerSection = document.querySelector(SECTION_SELECTOR);
    var sectionEl = drawerSection ? drawerSection.closest('[id^="shopify-section-"]') : null;
    var sectionId = sectionEl ? sectionEl.id.replace('shopify-section-', '') : null;

    var cartPromise = getCart();
    var sectionPromise = sectionId
      ? fetch('/?sections=' + sectionId).then(function (r) { return r.json(); })
      : Promise.resolve(null);

    return Promise.all([cartPromise, sectionPromise]).then(function (results) {
      var cart = results[0];
      var sections = results[1];

      // Re-render section HTML (line items, progress bar, empty state, footer)
      if (sections && sections[sectionId] && sectionEl) {
        var tmp = document.createElement('div');
        tmp.innerHTML = sections[sectionId];
        var newBody = tmp.querySelector('.oddit-cart-drawer__body');
        var oldBody = drawerSection.querySelector('.oddit-cart-drawer__body');
        if (newBody && oldBody) {
          oldBody.innerHTML = newBody.innerHTML;
        }
        var newFooter = tmp.querySelector('.oddit-cart-drawer__footer');
        var oldFooter = drawerSection.querySelector('.oddit-cart-drawer__footer');
        if (newFooter && oldFooter) {
          oldFooter.innerHTML = newFooter.innerHTML;
        }
        // Re-toggle empty class
        drawerSection.classList.toggle('is-empty', cart.item_count === 0);
        // Re-init carousel with fresh DOM
        var state = getState();
        if (state) {
          initCarousel(drawerSection, state);
          fetchCrossSellRecommendations(drawerSection, state);
        }
      }

      // Update header cart count badges
      var countEls = document.querySelectorAll('[data-cart-count]');
      countEls.forEach(function (el) {
        el.textContent = '(' + cart.item_count + ')';
      });

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: cart }
      }));
    });
  }

  function formatMoney(cents) {
    return (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: window.Shopify && window.Shopify.currency && window.Shopify.currency.active ? window.Shopify.currency.active : 'USD'
    });
  }

  // Cart API methods
  function addToCart(variantId, quantity, properties) {
    var body = {
      id: variantId,
      quantity: quantity || 1
    };
    if (properties) {
      body.properties = properties;
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(function () {
        return refreshCart();
      });
  }

  function addMultipleToCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(function () {
        return refreshCart();
      });
  }

  function removeFromCart(lineItemKey) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineItemKey, quantity: 0 })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Remove failed');
        return res.json();
      });
  }

  function updateLineItemQuantity(lineItemKey, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineItemKey, quantity: quantity })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Update failed');
        return res.json();
      });
  }

  function getCart() {
    return fetch('/cart.js')
      .then(function (res) { return res.json(); });
  }

  function refreshCart() {
    return refreshCartUI();
  }

  function destroySection(section) {
    var id = section.id || 'default';
    var state = sectionState.get(id);
    if (!state) return;

    section.removeEventListener('click', handleSectionClick);
    document.removeEventListener('keydown', handleKeydown);

    // Clean up document-level mouse listeners from carousel
    if (state.documentMouseMove) {
      document.removeEventListener('mousemove', state.documentMouseMove);
    }
    if (state.documentMouseUp) {
      document.removeEventListener('mouseup', state.documentMouseUp);
    }

    sectionState.delete(id);
  }

  // Init all instances on page load
  document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);

  // Shopify theme editor integration
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });
})();